import { useEffect, useMemo, useState, type DragEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inr, type Status } from '@krit/core';
import { patchDeal, type DealDto } from '../lib/api.js';
import {
  ACTIVE_STAGES, CLOSED_STAGES, CLOSED_NOTE, STAGE_DOT, stageLabel, isClosedStage,
} from '../lib/stages.js';

// Re-exported so existing importers (Deals.tsx) keep working — the definition now lives in
// lib/stages.ts, shared with the deal-header stage picker.
export { isClosedStage };

/**
 * ClickUp-style CRM board for the sourcing pipeline: drag a deal card between stage columns to
 * change its status (PATCH /deals/:id), with the cache repainted optimistically.
 *
 * Lane layout mirrors how the fund actually works. The active pipeline runs left→right across the
 * top; the two *terminal* stages sit in collapsed strips underneath — `Invested` (shown as "Done",
 * it graduates to Portfolio monitoring) and `Not Interested`. That split matters at volume: over
 * time the overwhelming majority of sourced deals end in Not Interested, so those lanes are built
 * as collapsed, searchable, paged lists of compact rows rather than columns of cards. They never
 * render more than a page of rows at a time, however many thousands of deals sit behind them.
 */
const ALL_LANES: string[] = [...ACTIVE_STAGES, ...CLOSED_STAGES];
const label = stageLabel;

/** Rows/cards rendered before a "show more" — keeps a 2,000-deal lane cheap to open. */
const CLOSED_PAGE = 25;
const ACTIVE_PAGE = 50;

const FIT_PILL: Record<string, string> = {
  Fit: 'bg-green-50 text-green',
  Borderline: 'bg-amber-50 text-amber',
  Pass: 'bg-red-50 text-red',
};

/** Drop-target handlers a lane spreads onto its wrapper. */
interface DropProps {
  onDragOver: (e: DragEvent) => void;
  onDragLeave: (e: DragEvent) => void;
  onDrop: (e: DragEvent) => void;
}

interface CardProps {
  d: DealDto;
  canEdit: boolean;
  dragging: boolean;
  saving: boolean;
  onOpen: () => void;
  onMove: (stage: string) => void;
  onDragStart: (e: DragEvent) => void;
  onDragEnd: () => void;
}

export function CrmBoard({ deals, onOpen, canEdit }: { deals: DealDto[]; onOpen: (id: string) => void; canEdit: boolean }) {
  const qc = useQueryClient();
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [err, setErr] = useState('');

  const move = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Status }) => patchDeal(id, { status }),
    // Optimistic: the card lands in the new lane on drop, and the cache rolls back if the PATCH fails.
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: ['deals'] });
      const prev = qc.getQueryData<{ deals: DealDto[] }>(['deals']);
      qc.setQueryData<{ deals: DealDto[] }>(['deals'], (old) =>
        old ? { ...old, deals: old.deals.map((d) => (d.id === id ? { ...d, status } : d)) } : old,
      );
      return { prev };
    },
    onError: (e, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['deals'], ctx.prev);
      setErr(String((e as Error).message));
    },
    onSuccess: () => setErr(''),
    onSettled: () => { qc.invalidateQueries({ queryKey: ['deals'] }); },
  });

  // Any status present in the data but not in our lane list (e.g. Intake, if it ever surfaces here)
  // gets its own column rather than being silently dropped off the board.
  const activeLanes = useMemo(() => {
    const known = new Set<string>(ALL_LANES);
    const extra = [...new Set(deals.map((d) => d.status))].filter((s) => !known.has(s));
    return [...ACTIVE_STAGES, ...extra];
  }, [deals]);

  // Deals arrive most-recently-updated first (GET /deals orders by updatedAt desc), so grouping
  // preserves that order — the top of every lane is the freshest activity.
  const byStage = useMemo(() => {
    const m = new Map<string, DealDto[]>();
    for (const d of deals) {
      const list = m.get(d.status);
      if (list) list.push(d);
      else m.set(d.status, [d]);
    }
    return m;
  }, [deals]);

  function moveTo(id: string, stage: string) {
    const d = deals.find((x) => x.id === id);
    if (!d || d.status === stage) return;
    move.mutate({ id, status: stage as Status });
  }

  const dropProps = (stage: string): DropProps => ({
    onDragOver: (e) => {
      if (!canEdit) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setOver(stage);
    },
    onDragLeave: (e) => {
      // Ignore bubbling leaves from child cards — only clear when the pointer really exits the lane.
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver((s) => (s === stage ? null : s));
    },
    onDrop: (e) => {
      if (!canEdit) return;
      e.preventDefault();
      setOver(null);
      const id = e.dataTransfer.getData('text/plain') || dragId;
      setDragId(null);
      if (id) moveTo(id, stage);
    },
  });

  const cardProps = (d: DealDto): CardProps => ({
    d,
    canEdit,
    dragging: dragId === d.id,
    saving: move.isPending && move.variables?.id === d.id,
    onOpen: () => onOpen(d.id),
    onMove: (s) => moveTo(d.id, s),
    onDragStart: (e) => {
      e.dataTransfer.setData('text/plain', d.id); // Firefox needs a payload to start a drag
      e.dataTransfer.effectAllowed = 'move';
      setDragId(d.id);
    },
    onDragEnd: () => { setDragId(null); setOver(null); },
  });

  return (
    <div>
      {err && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-red bg-red-50 px-3 py-2 text-[12px] text-red">
          <span>Couldn't move the deal — {err}</span>
          <button onClick={() => setErr('')} aria-label="Dismiss" className="ml-auto font-bold">✕</button>
        </div>
      )}
      {!canEdit && <p className="mb-3 text-[11.5px] text-muted-2">Read-only role — stages can be viewed but not moved.</p>}

      {/* Active pipeline — left→right */}
      <div className="flex gap-3 overflow-x-auto pb-3 items-start">
        {activeLanes.map((st) => (
          <ActiveLane key={st} stage={st} items={byStage.get(st) ?? []} over={over === st}
            dropProps={dropProps(st)} cardProps={cardProps} />
        ))}
      </div>

      {/* Terminal lanes — collapsed at the bottom, since this is where the volume ends up */}
      <div className="mt-1 space-y-2">
        {CLOSED_STAGES.map((st) => (
          <ClosedLane key={st} stage={st} items={byStage.get(st) ?? []} over={over === st}
            dropProps={dropProps(st)} cardProps={cardProps} />
        ))}
      </div>
    </div>
  );
}

function ActiveLane({ stage, items, over, dropProps, cardProps }: {
  stage: string;
  items: DealDto[];
  over: boolean;
  dropProps: DropProps;
  cardProps: (d: DealDto) => CardProps;
}) {
  const [shown, setShown] = useState(ACTIVE_PAGE);
  const alloc = items.reduce((a, d) => a + (d.allocation || 0), 0);
  const visible = items.slice(0, shown);
  return (
    <section {...dropProps} className="w-[252px] flex-none">
      <header className="flex items-center gap-2 px-2.5 py-2">
        <span className={`w-2 h-2 rounded-full shrink-0 ${STAGE_DOT[stage] ?? 'bg-muted-2'}`} />
        <span className="font-bold text-[11.5px] text-navy uppercase tracking-[0.3px] truncate">{label(stage)}</span>
        <span className="px-1.5 rounded-full text-[10.5px] font-bold bg-navy-50 text-navy shrink-0">{items.length}</span>
        {alloc > 0 && <span className="ml-auto text-[10px] text-muted-2 mono shrink-0">{inr(alloc)}</span>}
      </header>
      <div className={`rounded-xl border p-2 min-h-[120px] max-h-[62vh] overflow-y-auto transition-colors ${over ? 'border-teal bg-teal-50' : 'border-line bg-canvas'}`}>
        {items.length === 0 ? (
          <div className="border border-dashed border-line rounded-lg py-7 text-center text-[11px] text-muted-2">
            {over ? 'Drop here' : 'Empty'}
          </div>
        ) : (
          <>
            {visible.map((d) => <DealCard key={d.id} {...cardProps(d)} />)}
            {items.length > shown && (
              <button onClick={() => setShown((n) => n + ACTIVE_PAGE)}
                className="w-full py-1.5 rounded-lg text-[11px] font-semibold text-teal-d hover:bg-white">
                Show {Math.min(ACTIVE_PAGE, items.length - shown)} more · {items.length - shown} left
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}

/**
 * A terminal lane (Done / Not Interested). Collapsed by default and remembered per stage, because
 * these grow without bound. Expanded, it's a filterable list of compact rows, paged and scrolled —
 * so opening a lane holding thousands of dropped deals mounts 25 rows, not 2,000 cards.
 */
function ClosedLane({ stage, items, over, dropProps, cardProps }: {
  stage: string;
  items: DealDto[];
  over: boolean;
  dropProps: DropProps;
  cardProps: (d: DealDto) => CardProps;
}) {
  const key = `krit.deals.board.lane.${stage}`;
  const [open, setOpen] = useState(() => localStorage.getItem(key) === 'open');
  const [q, setQ] = useState('');
  const [shown, setShown] = useState(CLOSED_PAGE);
  useEffect(() => { localStorage.setItem(key, open ? 'open' : 'closed'); }, [key, open]);
  useEffect(() => { setShown(CLOSED_PAGE); }, [q]); // a new filter starts back at page one

  const alloc = items.reduce((a, d) => a + (d.allocation || 0), 0);
  const matches = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return items;
    return items.filter((d) => `${d.short} ${d.company} ${d.sector} ${d.poc} ${d.banker} ${d.location}`.toLowerCase().includes(ql));
  }, [items, q]);
  const visible = matches.slice(0, shown);

  return (
    <section {...dropProps} className={`rounded-xl border transition-colors ${over ? 'border-teal bg-teal-50' : 'border-line bg-card'}`}>
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left">
        <span className={`text-[9px] text-muted-2 transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
        <span className={`w-2 h-2 rounded-full shrink-0 ${STAGE_DOT[stage] ?? 'bg-muted-2'}`} />
        <span className="font-bold text-[11.5px] text-navy uppercase tracking-[0.3px]">{label(stage)}</span>
        <span className="px-1.5 rounded-full text-[10.5px] font-bold bg-navy-50 text-navy">{items.length}</span>
        <span className="text-[10.5px] text-muted-2 truncate hidden sm:inline">{CLOSED_NOTE[stage]}</span>
        <span className="flex-1" />
        {alloc > 0 && <span className="text-[10px] text-muted-2 mono shrink-0 hidden sm:inline">{inr(alloc)}</span>}
        <span className="text-[11px] font-semibold text-teal-d shrink-0">{open ? 'Collapse' : 'Expand'}</span>
      </button>

      {open && (
        <div className="px-3 pb-3">
          {items.length === 0 ? (
            <div className="border border-dashed border-line rounded-lg py-5 text-center text-[11px] text-muted-2">{over ? 'Drop here' : 'Nothing here yet'}</div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-2">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Filter ${label(stage).toLowerCase()}…`}
                  className="w-full max-w-[280px] px-2.5 py-1.5 border border-line rounded-lg bg-white text-[12px] outline-none focus:border-teal" />
                <span className="text-[10.5px] text-muted-2 mono">
                  {q ? `${matches.length} of ${items.length}` : `${items.length} deals`}
                </span>
              </div>
              <div className="max-h-[340px] overflow-y-auto rounded-lg border border-line-2 divide-y divide-line-2">
                {matches.length === 0 ? (
                  <div className="py-5 text-center text-[11px] text-muted-2">No match in this lane.</div>
                ) : (
                  visible.map((d) => <ClosedRow key={d.id} {...cardProps(d)} />)
                )}
              </div>
              {matches.length > shown && (
                <button onClick={() => setShown((n) => n + CLOSED_PAGE)}
                  className="mt-2 px-3 py-1.5 rounded-lg text-[11.5px] font-semibold text-teal-d border border-line hover:bg-canvas">
                  Show {Math.min(CLOSED_PAGE, matches.length - shown)} more · {matches.length - shown} left
                </button>
              )}
            </>
          )}
        </div>
      )}
      {!open && over && <div className="px-3 pb-2.5 text-[11px] font-semibold text-teal-d">Drop to mark {label(stage).toLowerCase()}</div>}
    </section>
  );
}

/** One line per deal — the density a few thousand dropped deals need. */
function ClosedRow({ d, canEdit, dragging, saving, onOpen, onMove, onDragStart, onDragEnd }: CardProps) {
  const [menu, setMenu] = useState(false);
  return (
    <div
      draggable={canEdit}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      role="button"
      tabIndex={0}
      aria-label={`${d.company} — ${label(d.status)}`}
      className={`relative flex items-center gap-2 px-2.5 py-2 bg-card text-[11.5px] hover:bg-canvas focus:outline-none focus:ring-2 focus:ring-teal/30 ${canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${dragging ? 'opacity-40' : ''} ${saving ? 'animate-pulse' : ''}`}
    >
      <span className="font-bold text-navy truncate max-w-[240px] shrink-0" title={d.company}>{d.company}</span>
      <span className="text-muted truncate flex-1 min-w-0" title={`${d.sector} · ${d.location}`}>{d.sector}</span>
      <span className="text-muted-2 truncate hidden md:inline max-w-[110px]" title={d.banker}>{d.banker || 'Direct'}</span>
      <span className="text-muted-2 truncate hidden lg:inline max-w-[90px]">{d.poc}</span>
      <span className="mono text-muted-2 shrink-0 hidden sm:inline">{d.dateCRM}</span>
      <span className="mono text-navy font-bold shrink-0 w-[52px] text-right">{inr(d.allocation)}</span>
      {canEdit && (
        <button onClick={(e) => { e.stopPropagation(); setMenu((m) => !m); }}
          aria-label={`Move ${d.company} to another stage`} aria-expanded={menu}
          className="shrink-0 px-1 text-muted-2 hover:text-navy text-[13px] leading-none">⋯</button>
      )}
      {menu && <StageMenu current={d.status} onPick={(s) => { setMenu(false); onMove(s); }} onClose={() => setMenu(false)} />}
    </div>
  );
}

function DealCard({ d, canEdit, dragging, saving, onOpen, onMove, onDragStart, onDragEnd }: CardProps) {
  const [menu, setMenu] = useState(false);
  const c = d.computed;
  return (
    <div
      draggable={canEdit}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      role="button"
      tabIndex={0}
      aria-label={`${d.company} — ${label(d.status)}`}
      className={`relative bg-card border border-line rounded-lg p-2.5 mb-2 last:mb-0 shadow-sh transition-shadow hover:shadow-sh2 focus:outline-none focus:ring-2 focus:ring-teal/30 ${canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${dragging ? 'opacity-40' : ''} ${saving ? 'animate-pulse' : ''}`}
    >
      <div className="flex items-start gap-1.5">
        <div className="min-w-0 flex-1">
          {/* Full registered name is the label; the short code stays the internal key (#1). */}
          <div className="font-bold text-navy text-[12.5px] leading-tight line-clamp-2" title={d.company}>{d.company}</div>
          <div className="text-[10.5px] text-muted truncate">{d.sector}{d.location ? ` · ${d.location}` : ''}</div>
        </div>
        <span className={`inline-flex px-1.5 py-px rounded-full text-[9px] font-bold shrink-0 ${FIT_PILL[c.thesisFit] ?? ''}`}>{c.thesisFit}</span>
        {canEdit && (
          <button onClick={(e) => { e.stopPropagation(); setMenu((m) => !m); }}
            aria-label={`Move ${d.company} to another stage`} aria-expanded={menu}
            className="shrink-0 -mt-0.5 px-1 text-muted-2 hover:text-navy text-[13px] leading-none">⋯</button>
        )}
      </div>

      <div className="flex items-center gap-1.5 mt-2 text-[10.5px] text-muted">
        <span className="truncate" title={d.banker}>{d.banker || 'Direct'}</span>
        <span className="ml-auto shrink-0 px-1.5 rounded-full bg-navy-50 text-navy font-bold mono" title="Krit score">{c.score}</span>
      </div>
      <div className="mt-1 text-[10.5px] text-muted mono">Ask {inr(d.valuationAsk)} · Alloc <b className="text-navy">{inr(d.allocation)}</b></div>
      {d.poc && <div className="mt-1 text-[10px] text-muted-2 truncate">POC {d.poc}</div>}

      {menu && <StageMenu current={d.status} onPick={(s) => { setMenu(false); onMove(s); }} onClose={() => setMenu(false)} />}
    </div>
  );
}

/** Click/keyboard/touch alternative to dragging — the same lanes, as a menu. */
function StageMenu({ current, onPick, onClose }: { current: string; onPick: (stage: string) => void; onClose: () => void }) {
  return (
    <>
      <div className="fixed inset-0 z-20" onClick={(e) => { e.stopPropagation(); onClose(); }} />
      <div role="menu" onClick={(e) => e.stopPropagation()}
        className="absolute right-1.5 top-7 z-30 w-[178px] bg-card border border-line rounded-lg shadow-sh2 py-1">
        <div className="px-2.5 pb-1 text-[9.5px] font-bold uppercase tracking-[0.4px] text-muted-2">Move to</div>
        {ALL_LANES.map((s, i) => (
          <button key={s} role="menuitem" onClick={(e) => { e.stopPropagation(); onPick(s); }}
            className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-[11.5px] hover:bg-canvas ${s === current ? 'font-bold text-navy' : 'text-muted'} ${i === ACTIVE_STAGES.length ? 'border-t border-line-2 mt-1 pt-2' : ''}`}>
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STAGE_DOT[s] ?? 'bg-muted-2'}`} />
            <span className="truncate">{label(s)}</span>
            {s === current && <span className="ml-auto text-teal-d">✓</span>}
          </button>
        ))}
      </div>
    </>
  );
}
